import React, { useCallback, useEffect, useState } from 'react'

import { Flex, Icon, Link, Select, Skeleton, theme } from '@driftt/dds'
import { faVideoSlash } from '@driftt/dds.icons/solid/faVideoSlash'

import {
  ConferenceProvider,
  getConferenceProviderStates,
  ProviderStates,
} from 'api/conferenceProviders'
import ConferenceTypeIcon from 'components/ConferenceTypeIcon'

interface VideoConferenceOption {
  label: string
  value: ConferenceProvider
  prefix: JSX.Element
}

const AllVideoConferenceOptions = [
  {
    label: 'Zoom',
    value: ConferenceProvider.Zoom,
    prefix: <ConferenceTypeIcon type={ConferenceProvider.Zoom} />,
  },
  {
    label: 'Google Meet',
    value: ConferenceProvider.GoogleMeet,
    prefix: <ConferenceTypeIcon type={ConferenceProvider.GoogleMeet} />,
  },
  {
    label: 'Microsoft Teams',
    value: ConferenceProvider.MicrosoftTeams,
    prefix: <ConferenceTypeIcon type={ConferenceProvider.MicrosoftTeams} />,
  },
  {
    label: 'None',
    value: ConferenceProvider.None,
    prefix: <Icon icon={faVideoSlash} />,
  },
] as VideoConferenceOption[]

interface VideoConferenceSelectProps {
  defaultConferenceType: ConferenceProvider
  updateState: (newValue: ConferenceProvider) => void
  disabled: boolean
  isOrgMeeting: boolean
}

const getConferenceProviderOptions = (
  isOrgMeeting?: boolean,
  providers?: ProviderStates
) => {
  return Object.entries(providers || {}).reduce(
    (acc, [k, v]) => {
      if (isOrgMeeting) {
        return v.enabled ? [...acc, k as ConferenceProvider] : acc
      } else {
        return v.enabled && v.connected
          ? [...acc, k as ConferenceProvider]
          : acc
      }
    },
    [ConferenceProvider.None] as ConferenceProvider[]
  )
}

const VideoConferencingSelect = ({
  defaultConferenceType,
  updateState,
  disabled,
  isOrgMeeting,
}: VideoConferenceSelectProps) => {
  const [conferenceProviders, setConferenceProviders] =
    useState<ProviderStates>()
  const [isLoading, setIsLoading] = useState(false)

  const getCalendarProviders = useCallback(async () => {
    setConferenceProviders(await getConferenceProviderStates())
  }, [])

  useEffect(() => {
    setIsLoading(true)
    getCalendarProviders().finally(() => {
      setIsLoading(false)
    })
  }, [getCalendarProviders])

  const providers = getConferenceProviderOptions(
    isOrgMeeting,
    conferenceProviders
  )

  const conferenceOptions = AllVideoConferenceOptions.filter((option) =>
    providers.includes(option.value)
  )
  const defaultOption =
    conferenceOptions.find(
      (option) => option.value === defaultConferenceType
    ) ?? AllVideoConferenceOptions[3]

  const handleSelectionChange = (option: VideoConferenceOption) => {
    updateState(option.value)
  }
  return (
    <Skeleton isLoading={isLoading}>
      <Flex direction="column">
        <Select
          size={'x-large'}
          label="Video conferencing"
          description="Details will be included in meeting invites"
          options={conferenceOptions}
          defaultValue={defaultOption}
          isClearable={false}
          // @ts-expect-error newValue in onChange is unknown
          onChange={handleSelectionChange}
          isDisabled={disabled}
        />
        <Link css={{ color: theme.colors.blue8 }} href="/settings2/meetings">
          Manage video conferencing settings
        </Link>
      </Flex>
    </Skeleton>
  )
}

export default VideoConferencingSelect
